import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('rankingsService')

/**
 * Service for managing global rankings and leaderboards
 */
class RankingsService {
  /**
   * Valid strategy types
   */
  static STRATEGY_TYPES = [
    'SAT_MATH',
    'SAT_MATH_MINI',
    'SAT_READING_WRITING',
    'SAT_READING_WRITING_MINI'
  ]

  /**
   * Time period options for filtering
   */
  static TIME_PERIODS = {
    ALL_TIME: 'OVERALL_BEST',
    THIS_YEAR: 'TOP_OF_YEAR',
    THIS_MONTH: 'TOP_OF_MONTH',
    THIS_WEEK: 'TOP_OF_WEEK'
  }

  /**
   * Get leaderboard for a specific strategy type and time period
   * @param {string} strategyType - The strategy type (e.g., 'SAT_MATH')
   * @param {Object} options - Query options
   * @param {string} options.period - Time period (OVERALL_BEST, TOP_OF_YEAR, etc.)
   * @param {number} options.limit - Max entries to return
   * @param {number} options.offset - Pagination offset
   * @param {string} options.country - Filter by country code
   * @param {string} options.region - Filter by region code
   * @param {number} options.minLevel - Filter by minimum level
   * @param {number} options.maxLevel - Filter by maximum level
   * @param {number} options.minScore - Filter by minimum score
   * @param {number} options.maxScore - Filter by maximum score
   * @param {string} options.search - Search by username
   * @returns {Promise<Object>} Leaderboard data with entries and pagination
   */
  async getLeaderboard(strategyType, options = {}) {
    try {
      const {
        period = RankingsService.TIME_PERIODS.ALL_TIME,
        limit = 50,
        offset = 0,
        country,
        region,
        minLevel,
        maxLevel,
        minScore,
        maxScore,
        search
      } = options

      const params = new URLSearchParams()
      params.append('strategyType', strategyType)
      params.append('limit', limit)
      params.append('offset', offset)
      if (country) params.append('country', country)
      if (region) params.append('region', region)
      if (minLevel) params.append('minLevel', minLevel)
      if (maxLevel) params.append('maxLevel', maxLevel)
      if (minScore) params.append('minScore', minScore)
      if (maxScore) params.append('maxScore', maxScore)
      if (search) params.append('search', search)

      const url = `/rankings/leaderboard/${period}?${params.toString()}`
      const response = await api.get(url)

      return response.data?.data || { entries: [], pagination: {} }
    } catch (error) {
      logger.error(`Error fetching leaderboard for ${strategyType}:`, error)
      return { entries: [], pagination: {} }
    }
  }

  /**
   * Get current user's ranking summary across all strategy types
   * @returns {Promise<Object>} User's ranking summary for each strategy type
   */
  async getMyRankingSummary() {
    try {
      const response = await api.get('/rankings/me')
      return response.data?.data || {}
    } catch (error) {
      logger.error('Error fetching user ranking summary:', error)
      return {}
    }
  }

  /**
   * Get user's best rankings for a specific strategy type
   * @param {string} strategyType - The strategy type
   * @returns {Promise<Object>} User's best rankings
   */
  async getMyRankings(strategyType) {
    try {
      const params = strategyType ? `?strategyType=${strategyType}` : ''
      const response = await api.get(`/rankings/me${params}`)
      return response.data?.data || {}
    } catch (error) {
      logger.error(`Error fetching user rankings for ${strategyType}:`, error)
      return {}
    }
  }

  /**
   * Get user's position in a filtered leaderboard
   * @param {string} strategyCode - The strategy code (e.g., 'OVERALL_BEST')
   * @param {string} strategyType - The strategy type (e.g., 'SAT_MATH')
   * @param {Object} options - Filter options
   * @param {number} options.minLevel - Filter by minimum level
   * @param {number} options.maxLevel - Filter by maximum level
   * @returns {Promise<{rank: number, total: number} | null>} User's position or null if not ranked
   */
  async getMyPosition(strategyCode, strategyType, options = {}) {
    try {
      const params = new URLSearchParams()
      params.append('strategyType', strategyType)
      if (options.minLevel) params.append('minLevel', options.minLevel)
      if (options.maxLevel) params.append('maxLevel', options.maxLevel)

      const response = await api.get(`/rankings/me/position/${strategyCode}?${params.toString()}`)
      return response.data?.data || null
    } catch (error) {
      logger.error('Error fetching user position:', error)
      return null
    }
  }

  /**
   * Get available ranking strategies
   * @returns {Promise<Array>} Array of strategy definitions
   */
  async getStrategies() {
    try {
      const response = await api.get('/rankings/strategies')
      return response.data?.data || []
    } catch (error) {
      logger.error('Error fetching ranking strategies:', error)
      return []
    }
  }

  /**
   * Get public profile for a user by their publicName
   * @param {string} publicName - The user's public alias
   * @returns {Promise<Object>} Public profile data
   */
  async getPublicProfile(publicName) {
    try {
      const response = await api.get(`/rankings/profile/${encodeURIComponent(publicName)}`)
      return response.data?.data || null
    } catch (error) {
      logger.error(`Error fetching public profile for ${publicName}:`, error)
      const err = new Error(error.response?.data?.message || 'Profile not found')
      err.status = error.response?.status || 500
      throw err
    }
  }

  /**
   * Get a user's position in the leaderboard by their publicName (public endpoint)
   * Returns the user's rank, total participants, and surrounding leaderboard entries
   * @param {string} publicName - The user's public alias
   * @param {string} strategyType - The strategy type (e.g., 'SAT_MATH_MINI')
   * @param {string} strategyCode - The strategy code (default: 'OVERALL_BEST')
   * @returns {Promise<Object>} User position and surrounding entries
   */
  async getUserPositionByName(publicName, strategyType, strategyCode = 'OVERALL_BEST') {
    try {
      const params = new URLSearchParams()
      params.append('strategyType', strategyType)
      params.append('strategyCode', strategyCode)

      const response = await api.get(
        `/rankings/user-position/${encodeURIComponent(publicName)}?${params.toString()}`
      )
      return response.data?.data || null
    } catch (error) {
      logger.error(`Error fetching user position for ${publicName}:`, error)
      return null
    }
  }

  /**
   * Update user's leaderboard visibility setting
   * @param {boolean} showOnLeaderboard - Whether to show on leaderboards
   * @returns {Promise<Object>} Updated user data
   */
  async updateLeaderboardVisibility(showOnLeaderboard) {
    try {
      const response = await api.put('/rankings/visibility', { showOnLeaderboard })
      return response.data?.data || {}
    } catch (error) {
      logger.error('Error updating leaderboard visibility:', error)
      throw error
    }
  }

  /**
   * Get formatted strategy name from strategy type
   * @param {string} strategyType - The strategy type constant
   * @returns {string} Human-readable strategy name
   */
  getStrategyDisplayName(strategyType) {
    const nameMap = {
      SAT_MATH: 'SAT Math',
      SAT_MATH_MINI: 'SAT Math Mini',
      SAT_READING_WRITING: 'SAT Reading & Writing',
      SAT_READING_WRITING_MINI: 'SAT R&W Mini',
      REFERRALS: 'Referrals'
    }
    return nameMap[strategyType] || strategyType
  }

  /**
   * Get strategy icon/emoji
   * @param {string} strategyType - The strategy type constant
   * @returns {string} Icon/emoji for the strategy
   */
  getStrategyIcon(strategyType) {
    const iconMap = {
      SAT_MATH: '🧮',
      SAT_MATH_MINI: '⚡',
      SAT_READING_WRITING: '📖',
      SAT_READING_WRITING_MINI: '✏️',
      REFERRALS: '🎁'
    }
    return iconMap[strategyType] || '📝'
  }

  /**
   * Get time period display name
   * @param {string} period - The time period code
   * @returns {string} Human-readable period name
   */
  getTimePeriodDisplayName(period) {
    const nameMap = {
      OVERALL_BEST: 'All Time',
      TOP_OF_YEAR: 'This Year',
      TOP_OF_MONTH: 'This Month',
      TOP_OF_WEEK: 'This Week'
    }
    return nameMap[period] || period
  }

  /**
   * Format rank for display with medal for top 3
   * @param {number} rank - The rank number
   * @returns {Object} Object with display text and medal info
   */
  formatRank(rank) {
    if (rank === 1) return { display: '🥇', isMedal: true, color: 'text-yellow-500' }
    if (rank === 2) return { display: '🥈', isMedal: true, color: 'text-gray-400' }
    if (rank === 3) return { display: '🥉', isMedal: true, color: 'text-amber-600' }
    return { display: `${rank}`, isMedal: false, color: 'text-gray-600' }
  }

  /**
   * Calculate percentile from rank and total
   * @param {number} rank - User's rank
   * @param {number} total - Total participants
   * @returns {number} Percentile (0-100, higher is better)
   */
  calculatePercentile(rank, total) {
    if (!total || total === 0) return 0
    return Math.round(((total - rank + 1) / total) * 100 * 10) / 10
  }

  /**
   * Get percentile color class
   * @param {number} percentile - Percentile value
   * @returns {string} Tailwind color class
   */
  getPercentileColor(percentile) {
    if (percentile >= 99) return 'text-yellow-500' // Top 1%
    if (percentile >= 95) return 'text-purple-500' // Top 5%
    if (percentile >= 90) return 'text-blue-500' // Top 10%
    if (percentile >= 75) return 'text-green-500' // Top 25%
    if (percentile >= 50) return 'text-yellow-600' // Top 50%
    return 'text-gray-500'
  }

  /**
   * Format time in mm:ss format
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string
   */
  formatTime(seconds) {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  /**
   * Format score for display based on strategy type
   * - For SAT strategies: shows "score/max" (e.g., "632/650")
   * - Falls back to percentage if SAT score not available
   *
   * @param {Object} entry - Leaderboard entry or ranking data
   * @param {string} entry.strategyType - Strategy type (e.g., 'SAT_MATH')
   * @param {number} entry.satScore - SAT score achieved (200-800)
   * @param {number} entry.satScoreMax - Maximum possible SAT score
   * @param {number} entry.primaryScore - Percentage score (0-100)
   * @returns {string} Formatted score string
   */
  formatScore(entry) {
    if (!entry) return '--'

    // SAT strategies use score/max format
    if (entry.satScore != null && entry.satScoreMax != null) {
      return `${entry.satScore}/${entry.satScoreMax}`
    }

    // Fallback to percentage if SAT score not available
    const score = entry.primaryScore ?? 0
    return `${score.toFixed(1)}%`
  }

  /**
   * Get referral leaderboard
   * @param {Object} options - Query options
   * @param {number} options.limit - Max entries to return
   * @param {number} options.offset - Pagination offset
   * @returns {Promise<Object>} Leaderboard data with entries and pagination
   */
  async getReferralLeaderboard(options = {}) {
    try {
      const { limit = 50, offset = 0 } = options
      const params = new URLSearchParams()
      params.append('limit', limit)
      params.append('offset', offset)

      const response = await api.get(`/referrals/leaderboard?${params.toString()}`)
      return response.data?.data || { entries: [], pagination: {} }
    } catch (error) {
      logger.error('Error fetching referral leaderboard:', error)
      return { entries: [], pagination: {} }
    }
  }
}

export default new RankingsService()
